import axios from 'axios';

const api = axios.create({
  baseURL: (process.env.REACT_APP_API_URL || 'http://localhost:5000') + '/api',
  // AUDIT-21: refresh_token httpOnly cookie'sinin /auth/refresh ve /auth/logout
  // çağrılarında gönderilebilmesi için global withCredentials açık.
  withCredentials: true,
});

api.interceptors.request.use(config => {
  // Branch impersonation token (tab-scoped) takes priority over persistent token
  const token = sessionStorage.getItem('restaflex_branch_token') || localStorage.getItem('restaflex_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// AUDIT-21: Single-flight refresh — birden fazla 401 paralel gelirse tek refresh çağrısı paylaşılır.
let refreshInflight = null;
async function doRefresh() {
  if (!refreshInflight) {
    refreshInflight = axios
      .post(api.defaults.baseURL + '/auth/refresh', {}, { withCredentials: true })
      .then((r) => {
        const newToken = r?.data?.token;
        if (newToken) {
          localStorage.setItem('restaflex_token', newToken);
          if (r.data.user) localStorage.setItem('restaflex_user', JSON.stringify(r.data.user));
        }
        return newToken;
      })
      .finally(() => { refreshInflight = null; });
  }
  return refreshInflight;
}

// FIX-08: Backend iki farklı hata formatı döndürebiliyor — `{ error }` (eski) ve
// `{ success: false, message }` (yeni). Component'ler tek bir alan okuyabilsin
// diye burada normalize ediyoruz: `message` yoksa `error`'dan doldur, yoksa
// status-text fallback. Orijinal alanlar korunur, sadece `message` garanti edilir.
function normalizeErrorData(data, fallback) {
  if (!data || typeof data !== 'object') return data;
  if (!data.message) {
    if (typeof data.error === 'string') data.message = data.error;
    else if (fallback) data.message = fallback;
  }
  return data;
}

// FIX-09: Hafif retry — GET için transient hata (network down / 503) durumunda 1 kez 500ms sonra tekrar dene.
// POST/PUT/DELETE asla retry edilmez (idempotency yok). 401/4xx retry edilmez.
api.interceptors.response.use(
  res => res,
  async err => {
    const config = err.config;
    if (!config || config.__retried) return Promise.reject(err);
    const isGet = config.method?.toLowerCase() === 'get';
    const isRetryable = !err.response || err.response.status === 503;
    if (isGet && isRetryable) {
      config.__retried = true;
      await new Promise(r => setTimeout(r, 500));
      return api(config);
    }
    return Promise.reject(err);
  }
);

api.interceptors.response.use(
  res => res,
  async err => {
    const original = err.config || {};
    const status = err.response?.status;
    const url = original.url || '';
    const isAuthFlow = url.includes('/auth/refresh') || url.includes('/auth/login') || url.includes('/auth/logout');

    // AUDIT-21: 401 → tek seferlik /auth/refresh dene, başarılıysa orijinal isteği tekrarla.
    // Branch impersonation token kullanan tab'larda refresh denemesi atlanır (chain cookie'si ile karışmasın).
    if (status === 401 && !original._retry && !isAuthFlow && !sessionStorage.getItem('restaflex_branch_token')) {
      original._retry = true;
      try {
        const newToken = await doRefresh();
        if (newToken) {
          original.headers = original.headers || {};
          original.headers.Authorization = `Bearer ${newToken}`;
          return api(original);
        }
      } catch { /* fall through to logout */ }
    }

    if (status === 401) {
      if (sessionStorage.getItem('restaflex_branch_token')) {
        sessionStorage.removeItem('restaflex_branch_token');
        sessionStorage.removeItem('restaflex_branch_user');
      } else {
        localStorage.removeItem('restaflex_token');
        localStorage.removeItem('restaflex_user');
      }
      window.location.href = '/login';
    }
    if (err.response) {
      err.response.data = normalizeErrorData(err.response.data, err.response.statusText);
    }
    return Promise.reject(err);
  }
);

/**
 * Component helper — API hatasından görüntülenebilir mesaj çıkarır.
 * Interceptor normalize ettiği için genelde `err.response.data.message` dolu olur,
 * ama network/timeout gibi `response` olmayan durumlar için fallback zinciri de var.
 *
 *   catch (err) {
 *     setError(getErrorMessage(err, t('somethingWentWrong')));
 *   }
 */
export function getErrorMessage(err, fallback = 'Bir hata oluştu') {
  if (!err) return fallback;
  const data = err.response?.data;
  if (data?.message) return data.message;
  if (typeof data?.error === 'string') return data.error;
  if (err.message && err.message !== 'Network Error') return err.message;
  if (err.code === 'ERR_NETWORK' || err.message === 'Network Error') {
    return 'Ağ bağlantısı kurulamadı';
  }
  return fallback;
}

// Auth
export const authAPI = {
  login: (email, password) => api.post('/auth/login', { email, password }),
  getMe: () => api.get('/auth/me'),
  changePassword: (data) => api.put('/auth/change-password', data),
  forgotPassword: (email) => api.post('/auth/forgot-password', { email }),
  resetPassword: (token, password) => api.post('/auth/reset-password', { token, password }),
  // IMP-02: 2FA
  setup2FA:   () => api.post('/auth/2fa/setup'),
  verify2FA:  (token) => api.post('/auth/2fa/verify', { token }),
  disable2FA: (password, token) => api.post('/auth/2fa/disable', { password, token }),
  login2FA:   (pre_token, code) => api.post('/auth/2fa/login', { pre_token, code }),
  // AUDIT-21: Refresh token
  refresh: () => api.post('/auth/refresh'),
  logout:  () => api.post('/auth/logout'),
};

// Tables
export const tablesAPI = {
  getAll: () => api.get('/tables'),
  create: (data) => api.post('/tables', data),
  update: (id, data) => api.put(`/tables/${id}`, data),
  delete: (id) => api.delete(`/tables/${id}`),
  getOrders: (id) => api.get(`/tables/${id}/orders`),
  reset: (id) => api.post(`/tables/${id}/reset`),
};

// Table Categories
export const tableCategoriesAPI = {
  getAll: () => api.get('/tables/categories'),
  create: (data) => api.post('/tables/categories', data),
  update: (id, data) => api.put(`/tables/categories/${id}`, data),
  delete: (id) => api.delete(`/tables/categories/${id}`),
};

// Menu
export const menuAPI = {
  getCategories: () => api.get('/menu/categories'),
  createCategory: (data) => api.post('/menu/categories', data),
  updateCategory: (id, data) => api.put(`/menu/categories/${id}`, data),
  deleteCategory: (id) => api.delete(`/menu/categories/${id}`),
  getItems: (params) => api.get('/menu/items', { params }),
  createItem: (data) => api.post('/menu/items', data),
  updateItem: (id, data) => api.put(`/menu/items/${id}`, data),
  deleteItem: (id) => api.delete(`/menu/items/${id}`),
  getPublicMenu: (params) => api.get('/menu/public', { params }),
  getPublicSettings: (params) => api.get('/menu/public-settings', { params }),
  aiScan: (data) => api.post('/menu/ai-scan', data),
  exportMenu: () => api.get('/menu/export', { responseType: 'blob' }),
  importMenu: (data) => api.post('/menu/import', data),
  /**
   * Ürün görseli yükler. FormData içinde `image` alanı zorunlu (multer.single('image')).
   * Content-Type axios tarafından otomatik set edilir — manuel set ETMEYIN (boundary bozulur).
   * @param {number|string} id - menu_item id
   * @param {FormData} formData - `image` alanı içeren FormData
   * @param {{ signal?: AbortSignal }} [opts] - opsiyonel iptal sinyali
   * @returns {Promise<import('axios').AxiosResponse<{success:boolean,data:{image_url:string}}>>}
   */
  uploadItemImage: (id, formData, opts = {}) =>
    api.post(`/menu/items/${id}/image`, formData, { signal: opts.signal }),
  /** @param {number|string} id @returns {Promise<import('axios').AxiosResponse>} */
  deleteItemImage: (id) => api.delete(`/menu/items/${id}/image`),
  /**
   * Restoran logosu yükler. FormData içinde `logo` alanı zorunlu (multer.single('logo')).
   * @param {FormData} formData - `logo` alanı içeren FormData
   * @param {{ signal?: AbortSignal }} [opts] - opsiyonel iptal sinyali
   * @returns {Promise<import('axios').AxiosResponse<{success:boolean,data:{qr_logo_url:string}}>>}
   */
  uploadLogo: (formData, opts = {}) =>
    api.post('/settings/logo', formData, { signal: opts.signal }),
  getQrCartMetrics: () => api.get('/menu/admin/qr-cart-metrics'),
};

// Orders
export const ordersAPI = {
  getAll: (params) => api.get('/orders', { params }),
  getById: (id) => api.get(`/orders/${id}`),
  create: (data) => api.post('/orders', data),
  addItem: (id, item) => api.post(`/orders/${id}/items`, item),
  removeItem: (orderId, itemId) => api.delete(`/orders/${orderId}/items/${itemId}`),
  updateStatus: (id, status) => api.patch(`/orders/${id}/status`, { status }),
  delete: (id) => api.delete(`/orders/${id}`),
  getPrepTimeEstimate: () => api.get('/orders/prep-time-estimate'), // IMP-10
};

// IMP-10 parça 3: Public (QR menü) hazırlık süresi
export const publicAPI = {
  getPrepTimeEstimate: (restaurantId) =>
    api.get('/menu/public-prep-time', { params: { restaurant_id: restaurantId } }),
};

// Payments
export const paymentsAPI = {
  process: (data) => api.post('/payments', data),
  tableCheckout: (tableId, data) => api.post(`/payments/table/${tableId}`, data),
  getDailyReport: (date) => api.get('/payments/daily-report', { params: { date } }),
  getSalesReport: (params) => api.get('/payments/sales-report', { params }),
};

// Stock
export const stockAPI = {
  getAll: (params) => api.get('/stock', { params }),
  create: (data) => api.post('/stock', data),
  update: (id, data) => api.put(`/stock/${id}`, data),
  delete: (id) => api.delete(`/stock/${id}`),
  getLowStock: () => api.get('/stock/low-stock'),
  adjust: (id, data) => api.post(`/stock/${id}/adjust`, data),
  getAdjustments: (id) => api.get(`/stock/${id}/adjustments`),
  getWasteSummary: (params) => api.get('/stock/waste-summary', { params }),
  getAllAdjustments: (params) => api.get('/stock/adjustments-all', { params }),
};

// Recipes
export const recipesAPI = {
  getAll: () => api.get('/recipes'),
  getByMenuItem: (id) => api.get(`/recipes/menu-item/${id}`),
  create: (data) => api.post('/recipes', data),
  update: (id, data) => api.put(`/recipes/${id}`, data),
  delete: (id) => api.delete(`/recipes/${id}`),
  getPortions: (id) => api.get(`/recipes/menu-item/${id}/portions`),
};

// Users
export const usersAPI = {
  getAll: () => api.get('/users'),
  create: (data) => api.post('/users', data),
  update: (id, data) => api.put(`/users/${id}`, data),
  delete: (id) => api.delete(`/users/${id}`),
  getPayroll: (id, params) => api.get(`/users/${id}/payroll`, { params }),
};

// Kitchen
export const kitchenAPI = {
  getOrders: () => api.get('/kitchen'),
  markReady: (id) => api.patch(`/kitchen/${id}/ready`),
  markServed: (id) => api.patch(`/kitchen/${id}/served`),
  getStats: () => api.get('/kitchen/stats'),
};

// Restaurants (superadmin)
export const restaurantsAPI = {
  getAll: () => api.get('/restaurants'),
  getById: (id) => api.get(`/restaurants/${id}`),
  create: (data) => api.post('/restaurants', data),
  update: (id, data) => api.put(`/restaurants/${id}`, data),
  delete: (id) => api.delete(`/restaurants/${id}`),
  getStats: (id) => api.get(`/restaurants/${id}/stats`),
  getAllStats: () => api.get('/restaurants/stats'),
};

// Plans
export const plansAPI = {
  getAll: () => api.get('/plans'),
  getById: (id) => api.get(`/plans/${id}`),
  create: (data) => api.post('/plans', data),
  update: (id, data) => api.put(`/plans/${id}`, data),
  delete: (id) => api.delete(`/plans/${id}`),
};

// Subscriptions
export const subscriptionsAPI = {
  getMy: () => api.get('/subscriptions/my'),
  startTrial: (planId) => api.post('/subscriptions/trial', { plan_id: planId }),
  changePlan: (data) => api.put('/subscriptions/change-plan', data),
  cancel: () => api.post('/subscriptions/cancel'),
  getHistory: () => api.get('/subscriptions/history'),
  getSaasStats: () => api.get('/subscriptions/saas-stats'),
  getAll: () => api.get('/subscriptions/all'),
};

// iyzico
export const iyzicoAPI = {
  createPayment: (data) => api.post('/iyzico/create-payment', data),
  getPaymentResult: (token) => api.get('/iyzico/payment-result', { params: { token } }),
};

// Stats
export const statsAPI = {
  getWaiters: (params) => api.get('/stats/waiters', { params }),
  getReports: (params) => api.get('/stats/reports', { params }),
};

// BI Reports
export const reportsAPI = {
  getHourly:           (params) => api.get('/reports/hourly',            { params }),
  getTopItems:         (params) => api.get('/reports/top-items',          { params }),
  getWaiterPerformance:(params) => api.get('/reports/waiter-performance', { params }),
  getDeliverySummary:  (params) => api.get('/reports/delivery-summary',   { params }),
  getCategoryRevenue:  (params) => api.get('/reports/category-revenue',   { params }),
  getStockValue:       (params) => api.get('/reports/stock-value',         { params }),
  getVatReport:        (params) => api.get('/reports/vat',                 { params }),
  getPaymentTrend:     (params) => api.get('/reports/payment-trend',       { params }),
  getFoodCost:         () =>       api.get('/reports/food-cost'),
  getCogs:             (params) => api.get('/reports/cogs',                { params }),
  getCustomerSegments: () =>       api.get('/reports/customer-segments'),
  getProfitLoss:       (params) => api.get('/reports/profit-loss',         { params }),
  getPayrollReport:    (params) => api.get('/reports/payroll',             { params }),
  getProfitLossTrend:  (params) => api.get('/reports/profit-loss-trend',   { params }),
};

// Settings
export const settingsAPI = {
  get: () => api.get('/settings'),
  update: (data) => api.put('/settings', data),
};

// UI-04: Bildirim merkezi
export const notificationsAPI = {
  getAll: () => api.get('/notifications'),
};

// IMP-01: Audit log görüntüleme
export const auditLogsAPI = {
  getAll: (params) => api.get('/audit-logs', { params }),
};

// AUDIT-14: Çöp kutusu / soft delete restore
export const trashAPI = {
  list: (type, params = {}) => api.get('/trash', { params: { type, ...params } }),
  restore: (type, id) => api.patch(`/trash/${type}/${id}/restore`),
};

// IMP-04: KDV grupları
export const taxGroupsAPI = {
  getAll: () => api.get('/tax-groups'),
  create: (data) => api.post('/tax-groups', data),
  update: (id, data) => api.put(`/tax-groups/${id}`, data),
  delete: (id) => api.delete(`/tax-groups/${id}`),
};

// IMP-15: Feature flags
export const featureFlagsAPI = {
  getAll: () => api.get('/feature-flags'),
  create: (data) => api.post('/feature-flags', data),
  update: (id, data) => api.put(`/feature-flags/${id}`, data),
  delete: (id) => api.delete(`/feature-flags/${id}`),
  check: (key) => api.get(`/feature-flags/check/${key}`),
};

// IMP-05: Multi-currency
export const currencyAPI = {
  getRates:    () => api.get('/currency/rates'),
  createRate:  (data) => api.post('/currency/rates', data),
  convert:     (params) => api.get('/currency/convert', { params }),
  getDefault:  () => api.get('/currency/default'),
  setDefault:  (currency) => api.put('/currency/default', { currency }),
};

// IMP-13: shiftsAPI zaten aşağıda (satır 345) tanımlı — duplicate kaldırıldı.

// Ürün Varyantları
export const variantsAPI = {
  getByMenuItem: (menuItemId) => api.get(`/menu-items/${menuItemId}/variants`),
  create: (menuItemId, data) => api.post(`/menu-items/${menuItemId}/variants`, data),
  update: (id, data) => api.put(`/menu-items/variants/${id}`, data),
  delete: (id) => api.delete(`/menu-items/variants/${id}`),
};

// Kampanya Kodları
export const promoCodesAPI = {
  getAll: () => api.get('/promo-codes'),
  create: (data) => api.post('/promo-codes', data),
  update: (id, data) => api.put(`/promo-codes/${id}`, data),
  delete: (id) => api.delete(`/promo-codes/${id}`),
  validate: (data) => api.post('/promo-codes/validate', data),
};

// Hediye Çekleri
export const giftVouchersAPI = {
  getAll: () => api.get('/gift-vouchers'),
  create: (data) => api.post('/gift-vouchers', data),
  validate: (data) => api.post('/gift-vouchers/validate', data),
  redeem: (id, data) => api.post(`/gift-vouchers/${id}/redeem`, data),
  delete: (id) => api.delete(`/gift-vouchers/${id}`),
};

// Müşteriler
export const customersAPI = {
  getAll: (params) => api.get('/customers', { params }),
  getById: (id) => api.get(`/customers/${id}`),
  create: (data) => api.post('/customers', data),
  update: (id, data) => api.put(`/customers/${id}`, data),
  delete: (id) => api.delete(`/customers/${id}`),
  addPoints: (id, data) => api.post(`/customers/${id}/loyalty/add`, data),
  redeemPoints: (id, data) => api.post(`/customers/${id}/loyalty/redeem`, data),
  getLedger: (id, params) => api.get(`/customers/${id}/ledger`, { params }),
  getSegmentationReport: () => api.get('/customers/segmentation-report'),
};

// CRM-01: Pazarlama Kampanyaları
export const marketingAPI = {
  getCampaigns: () => api.get('/marketing/campaigns'),
  createCampaign: (data) => api.post('/marketing/campaigns', data),
  sendCampaign: (id) => api.post(`/marketing/campaigns/${id}/send`),
  deleteCampaign: (id) => api.delete(`/marketing/campaigns/${id}`),
  getRecipientPreview: (params) => api.get('/marketing/recipient-preview', { params }),
};

// ERP-02: Tedarikçiler
export const suppliersAPI = {
  getAll: (params) => api.get('/suppliers', { params }),
  getById: (id) => api.get(`/suppliers/${id}`),
  create: (data) => api.post('/suppliers', data),
  update: (id, data) => api.put(`/suppliers/${id}`, data),
  delete: (id) => api.delete(`/suppliers/${id}`),
  getLedger: (id, params) => api.get(`/suppliers/${id}/ledger`, { params }),
};

// ERP-01: Kuryeler
export const couriersAPI = {
  getAll: (params) => api.get('/couriers', { params }),
  getById: (id) => api.get(`/couriers/${id}`),
  create: (data) => api.post('/couriers', data),
  update: (id, data) => api.put(`/couriers/${id}`, data),
  delete: (id) => api.delete(`/couriers/${id}`),
  updateLocation: (id, data) => api.post(`/couriers/${id}/location`, data),
  getStats: (id, params) => api.get(`/couriers/${id}/stats`, { params }),
};

// ERP-02 Parça 4: Satın alma siparişleri (PO)
export const purchaseOrdersAPI = {
  getAll: (params) => api.get('/purchase-orders', { params }),
  getById: (id) => api.get(`/purchase-orders/${id}`),
  create: (data) => api.post('/purchase-orders', data),
  update: (id, data) => api.put(`/purchase-orders/${id}`, data),
  markReceived: (id) => api.put(`/purchase-orders/${id}/receive`),
  delete: (id) => api.delete(`/purchase-orders/${id}`),
};

// ERP-07 Parça 1: Kasa/Banka hareketleri
export const cashTxAPI = {
  getAll: (params) => api.get('/cash-transactions', { params }),
  getById: (id) => api.get(`/cash-transactions/${id}`),
  create: (data) => api.post('/cash-transactions', data),
  update: (id, data) => api.put(`/cash-transactions/${id}`, data),
  delete: (id) => api.delete(`/cash-transactions/${id}`),
  syncPayments: (data) => api.post('/cash-transactions/sync-payments', data || {}),
};

// İadeler
export const refundsAPI = {
  getAll: (params) => api.get('/refunds', { params }),
  create: (paymentId, data) => api.post(`/refunds/payment/${paymentId}`, data),
};

// Toplu Fiyat Güncelleme
export const bulkPriceUpdateAPI = {
  update: (data) => api.put('/menu/bulk-price-update', data),
};

// Teslimat Platformları & Siparişleri
export const deliveryAPI = {
  getOrders: (params) => api.get('/integrations/delivery-orders', { params }),
  updateOrderStatus: (id, data) => api.put(`/integrations/delivery-orders/${id}/status`, data),
  assignCourier: (id, courierId) => api.put(`/integrations/delivery-orders/${id}/courier`, { courier_id: courierId }),
  autoAssignCourier: (id) => api.post(`/integrations/delivery-orders/${id}/auto-assign`),
  rateOrder: (id, rating) => api.post(`/integrations/delivery-orders/${id}/rate`, { rating }),
  getPlatforms: () => api.get('/integrations/platforms'),
  togglePlatform: (id) => api.post(`/integrations/platforms/${id}/toggle`),
  createPlatform: (data) => api.post('/integrations/platforms', data),
  updatePlatform: (id, data) => api.put(`/integrations/platforms/${id}`, data),
  testPlatformConnection: (id) => api.post(`/integrations/platforms/${id}/test`),
};

// POS — Yazarkasa
export const posAPI = {
  // Ürünler
  getProducts: (params) => api.get('/pos/products', { params }),
  getProductByBarcode: (code) => api.get(`/pos/products/barcode/${encodeURIComponent(code)}`),
  createProduct: (data) => api.post('/pos/products', data),
  updateProduct: (id, data) => api.put(`/pos/products/${id}`, data),
  deleteProduct: (id) => api.delete(`/pos/products/${id}`),

  // Satış
  completeSale: (data) => api.post('/pos/sale', data),
  getSales: (params) => api.get('/pos/sales', { params }),
  getSalesByUser: (params) => api.get('/pos/sales/by-user', { params }),
  getSaleByReceipt: (receipt) => api.get('/pos/sales/by-receipt', { params: { receipt } }),
  getSaleById: (saleId) => api.get(`/pos/sales/${saleId}`),
  refundSale: (saleId) => api.post(`/pos/sales/${saleId}/refund`),
  printReceipt: (saleId) => api.post(`/pos/sales/${saleId}/print`),
  importProducts: (data) => api.post('/pos/products/import', data),
  exportProducts: () => api.get('/pos/products/export', { responseType: 'blob' }),

  // Kasa oturumu
  openSession: (data) => api.post('/pos/session/open', data),
  closeSession: (data) => api.post('/pos/session/close', data),
  getCurrentSession: () => api.get('/pos/session/current'),
  getXReport: () => api.get('/pos/x-report'),
  getZReports: (params) => api.get('/pos/z-reports', { params }),

  // Sanal POS
  sanalPosCharge: (data) => api.post('/pos/sanal-pos/charge', data),
  getSanalPosResult: (id) => api.get(`/pos/sanal-pos/result/${id}`),
};

// Yazıcı Yönetimi
export const printersAPI = {
  getAll: () => api.get('/printers'),
  getById: (id) => api.get(`/printers/${id}`),
  create: (data) => api.post('/printers', data),
  update: (id, data) => api.put(`/printers/${id}`, data),
  delete: (id) => api.delete(`/printers/${id}`),
  getStatus: (id) => api.get(`/printers/${id}/status`),
  testPrint: (id) => api.post(`/printers/${id}/test`),
  getUsbDevices: () => api.get('/printers/usb/devices'),
  getPrintJobs: (params) => api.get('/printers/jobs', { params }),
  retryJob: (id) => api.post(`/printers/jobs/${id}/retry`),
};

// Yazdırma kuyruğu (rapor / belge yazdırma)
export const printerAPI = {
  printZReport: (data) => api.post('/print/z-report', data),
  printDailyReport: (data) => api.post('/print/daily-report', data),
};

// Online Sipariş (QR → Sepet → Ödeme)
export const onlineOrderAPI = {
  checkout: (data) => api.post('/online-orders/checkout', data),
  callback: (token) => api.post('/online-orders/callback', { token }),
  getStatus: (orderId) => api.get(`/online-orders/status/${orderId}`),
};

// Rezervasyon
export const reservationsAPI = {
  getAll: (params) => api.get('/reservations', { params }),
  getById: (id) => api.get(`/reservations/${id}`),
  create: (data) => api.post('/reservations', data),
  update: (id, data) => api.put(`/reservations/${id}`, data),
  updateStatus: (id, status) => api.patch(`/reservations/${id}/status`, { status }),
  delete: (id) => api.delete(`/reservations/${id}`),
  checkAvailability: (params) => api.get('/reservations/availability', { params }),
};

// Vardiya & Mesai
export const shiftsAPI = {
  getTemplates: () => api.get('/shifts/templates'),
  createTemplate: (data) => api.post('/shifts/templates', data),
  updateTemplate: (id, data) => api.put(`/shifts/templates/${id}`, data),
  deleteTemplate: (id) => api.delete(`/shifts/templates/${id}`),
  getAssignments: (params) => api.get('/shifts/assignments', { params }),
  createAssignment: (data) => api.post('/shifts/assignments', data),
  updateAssignment: (id, data) => api.put(`/shifts/assignments/${id}`, data),
  checkIn: (id) => api.patch(`/shifts/assignments/${id}/checkin`),
  checkOut: (id) => api.patch(`/shifts/assignments/${id}/checkout`),
  markAbsent: (id, notes) => api.patch(`/shifts/assignments/${id}/absent`, { notes }),
  deleteAssignment: (id) => api.delete(`/shifts/assignments/${id}`),
  getWeeklySummary: (week) => api.get('/shifts/summary', { params: { week } }),
  getBordroSummary: (week) => api.get('/shifts/bordro', { params: { week } }),
  getStaff: () => api.get('/shifts/staff'),
  copyWeek: (data) => api.post('/shifts/assignments/copy-week', data),
  autoGenerate: (data) => api.post('/shifts/assignments/auto-generate', data),
};

// Push Bildirimleri
export const pushAPI = {
  getVapidKey: () => api.get('/push/vapid-key'),
  register: (data) => api.post('/push/register', data),
  unregister: (data) => api.delete('/push/unregister', { data }),
  getTokens: () => api.get('/push/tokens'),
  sendTest: (data) => api.post('/push/test', data),
};

export const waiterCallAPI = {
  // Public — no auth required (QR menu)
  call: (data) => api.post('/waiter-calls', data),
  // Staff
  getCalls: (params) => api.get('/waiter-calls', { params }),
  updateStatus: (id, status) => api.put(`/waiter-calls/${id}/status`, { status }),
};

// HR — Personel Yönetimi
export const hrAPI = {
  // Employees
  getEmployees: () => api.get('/hr/employees'),
  getEmployee: (id) => api.get(`/hr/employees/${id}`),
  createEmployee: (data) => api.post('/hr/employees', data),
  bulkImportEmployees: (data) => api.post('/hr/employees/bulk-import', data),
  updateEmployee: (id, data) => api.put(`/hr/employees/${id}`, data),
  deleteEmployee: (id) => api.delete(`/hr/employees/${id}`),
  getSeverance: (id, params = {}) => {
    const qs = Object.keys(params).length ? `?${new URLSearchParams(params).toString()}` : '';
    return api.get(`/hr/employees/${id}/severance${qs}`);
  },
  getEmployeeShifts: (id, days = 30) => api.get(`/hr/employees/${id}/shifts`, { params: { days } }),
  // Payrolls
  getPayrolls: (params) => api.get('/hr/payrolls', { params }),
  calculatePayroll: (data) => api.post('/hr/payrolls/calculate', data),
  createPayroll: (data) => api.post('/hr/payrolls', data),
  bulkCalculate: (data) => api.post('/hr/payrolls/bulk', data),
  grossUpPayroll: (data) => api.post('/hr/payrolls/gross-up', data),
  approvePayroll: (id) => api.put(`/hr/payrolls/${id}/approve`),
  markPaid: (id) => api.put(`/hr/payrolls/${id}/mark-paid`),
  getAnnualSummary: (params) => api.get('/hr/payrolls/annual-summary', { params }),
  getBankTransfer: (year, month) => {
    return api.get('/hr/payrolls/bank-transfer', {
      params: { year, month },
      responseType: 'blob',
    });
  },
  getMuhtasarPDF: (year, month) => {
    return api.get('/hr/payrolls/muhtasar', {
      params: { year, month },
      responseType: 'blob',
    });
  },
  getSgkBildirgePDF: (year, month) => {
    return api.get('/hr/payrolls/sgk-bildirge', {
      params: { year, month },
      responseType: 'blob',
    });
  },
  downloadPayrollPDF: (id, opts = {}) => {
    const params = new URLSearchParams(opts).toString();
    const qs = params ? `?${params}` : '';
    return api.get(`/hr/payrolls/${id}/pdf${qs}`, { responseType: 'blob' });
  },
  // Leaves
  getLeaves: (params) => api.get('/hr/leaves', { params }),
  createLeave: (data) => api.post('/hr/leaves', data),
  bulkCreateLeaves: (data) => api.post('/hr/leaves/bulk', data),
  updateLeave: (id, data) => api.put(`/hr/leaves/${id}`, data),
  deleteLeave: (id) => api.delete(`/hr/leaves/${id}`),
  getLeaveBalance: (employeeId) => api.get(`/hr/leaves/balance/${employeeId}`),
  // Documents
  getDocuments: (params) => api.get('/hr/documents', { params }),
  createDocument: (data) => api.post('/hr/documents', data),
  deleteDocument: (id) => api.delete(`/hr/documents/${id}`),
};

// KVKK / GDPR
export const gdprAPI = {
  submitRequest: (data) => api.post('/gdpr/request', data),
  getMyRequests: () => api.get('/gdpr/my-requests'),
  exportMyData: () => api.get('/gdpr/my-data', { responseType: 'blob' }),
  getRequests: (params) => api.get('/gdpr/requests', { params }),
  processRequest: (id, data) => api.put(`/gdpr/requests/${id}/process`, data),
  saveCookieConsent: (data) => api.post('/gdpr/cookie-consent', data),
  getCookieStats: () => api.get('/gdpr/cookie-stats'),
};

// White-label
export const whiteLabelAPI = {
  getAll: () => api.get('/white-label'),
  getById: (id) => api.get(`/white-label/${id}`),
  getPublicBranding: (params) => api.get('/white-label/branding', { params }),
  create: (data) => api.post('/white-label', data),
  update: (id, data) => api.put(`/white-label/${id}`, data),
  delete: (id) => api.delete(`/white-label/${id}`),
  assignRestaurant: (id, restaurant_id) => api.post(`/white-label/${id}/assign`, { restaurant_id }),
  unassignRestaurant: (restaurant_id) => api.delete(`/white-label/restaurant/${restaurant_id}/unassign`),
};

// Zapier / Make.com Webhooks
export const webhookAPI = {
  getEvents: () => api.get('/webhooks/events'),
  getAll: () => api.get('/webhooks'),
  create: (data) => api.post('/webhooks', data),
  update: (id, data) => api.put(`/webhooks/${id}`, data),
  delete: (id) => api.delete(`/webhooks/${id}`),
  getDeliveries: (id, params) => api.get(`/webhooks/${id}/deliveries`, { params }),
  test: (id) => api.post(`/webhooks/${id}/test`),
};

// Manuel veritabanı yedekleme (sadece superadmin)
export const backupAPI = {
  runManual: () => api.post('/backup/run'),
};

export default api;
